import Big from 'big.js'
import { Fonts, Text, TextSize, Weight } from 'components'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks'
import React from 'react'
import styled, { css } from 'styled-components'
import { GLOBAL_COLORS } from 'theme'
import { getBlockExplorerToken, timeToDateFormatter } from 'utils'
import { Separator, SwitchContentWrapper, Title } from './styled'

import { ProductPageComponentProps } from '../../interfaces'
import { isAddressEqual } from 'viem'
import { ARTXAI, JMX } from 'products'
import { Product } from 'services/query-v2/products/index/product-info/model'

const AdditionalDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 250px;
  row-gap: 30px;
  column-gap: 0;
  flex-wrap: wrap;
  margin-top: 35px;
  ${({ theme }) => theme.mediaWidth.Mobile`
    height: auto;
    flex-wrap: nowrap;
  `}
`
const AdditionalDataItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const StyledTitle = styled(Text)`
  font-weight: ${Weight.Medium};
  font-size: ${TextSize.small};
  color: ${({ theme }) => theme.secondaryText};
  width: 100px;
`

const StyledValue = styled.span<{ isBlue: boolean }>`
  font-family: ${Fonts.DMSans};
  font-weight: ${Weight.Medium};
  font-size: ${TextSize.p};
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  ${({ isBlue }) =>
    isBlue &&
    css`
      color: ${GLOBAL_COLORS.coralBlue};
      text-decoration: underline;
      cursor: pointer;
      transition: 0.4s all;
      :hover {
        opacity: 0.7;
      }
      :active {
        opacity: 0.4;
      }
    `}
`

const MethodologyText = styled(Text)`
  font-weight: ${Weight.Medium};
  font-size: ${TextSize.small};
  color: ${({ theme }) => theme.secondaryText};
  margin-top: 35px;
`

const StyledSpan = styled.a`
  font-weight: ${Weight.Medium};
  font-size: ${TextSize.small};
  color: ${({ theme }) => theme.secondaryText};
  text-decoration: underline;
  cursor: pointer;
  transition: 0.4s all;
  :hover {
    opacity: 0.7;
  }
  :active {
    opacity: 0.4;
  }
`

const percentFromBasePoint = (fee: number) => {
  return Big(fee).mul(100).precision(2)
}

export const AdditionalInfoContent: React.FC<ProductPageComponentProps> = ({
  store,
  productMetadata,
}) => {
  const { product } = store
  const { network, blockExplorer, methodologyLink, address } = productMetadata
  const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.IndexDetails)

  const link = getBlockExplorerToken(address || '', network)

  const additionalData = [
    { name: 'Inception date', value: timeToDateFormatter(product.created) },
    {
      name: 'Rebalancing',
      value: isAddressEqual(productMetadata.productCurrency.address, ARTXAI.address)
        ? 'Active'
        : isAddressEqual(productMetadata.productCurrency.address, JMX.address)
          ? 'Every 2 weeks'
          : productMetadata.productCurrency.type === 'index-v2'
            ? 'Quarterly'
            : 'Monthly',
    },
    { name: 'Max capacity', value: 'None' },
    { name: 'Leverage', value: 'None' },
    { name: 'Block explorer', value: blockExplorer },
    {
      name: 'Minting fee',
      value: `${percentFromBasePoint(product.feeMint)}%`,
    },
    {
      name: 'Redemption fee',
      value: `${percentFromBasePoint(product.feeBurn)}%`,
    },
    {
      name: 'Mgmt. fee',
      value: `${percentFromBasePoint(product.feeAUMPercent)}%`,
    },
  ]

  const Methodology: React.FC<{ product: Product }> = ({ product }) => {
    if (isAddressEqual(product.address, ARTXAI.address)) {
      return (
        <MethodologyText>
          ARTXAI uses a proprietary methodology to dynamically and continuously rebalance its
          constituent assets based on prevailing onchain and offchain data.
        </MethodologyText>
      )
    }
    if (isAddressEqual(product.address, JMX.address)) {
      return (
        <MethodologyText>
          To find out more about the methodology visit the JOOCE
          <StyledSpan onClick={() => navigationEvent(methodologyLink, true)}>
            documentation.
          </StyledSpan>
        </MethodologyText>
      )
    }
    return (
      <MethodologyText>
        To find out more about our methodology, visit our{' '}
        <StyledSpan onClick={() => navigationEvent(methodologyLink, true)}>
          documentation.
        </StyledSpan>
      </MethodologyText>
    )
  }
  return (
    <SwitchContentWrapper>
      <Title>Additional information</Title>
      <AdditionalDataWrapper>
        {additionalData.map(({ name, value }) => (
          <AdditionalDataItem key={`${name}-${value}`}>
            <StyledTitle>{name}</StyledTitle>
            <StyledValue
              onClick={() => name === 'Block explorer' && navigationEvent(link, true)}
              isBlue={name === 'Block explorer'}
            >
              {value}
            </StyledValue>
          </AdditionalDataItem>
        ))}
      </AdditionalDataWrapper>
      <>
        <Separator />
        <Title>Methodology</Title>
        <Methodology product={product} />
      </>
    </SwitchContentWrapper>
  )
}
